import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { Phone as PhoneIcon, Search as SearchIcon, WarningTriangle } from 'iconoir-react'
import { debounce } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { ContactNameIcon } from 'app/components/contacts/ContactNameIcon'
import Spinner from 'app/components/Spinner'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { ContactJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { usPhoneNumber } from 'lib/phone'

export function AddConversationModal({
  conversations,
  onContactSelect,
}: {
  conversations: Record<string, number>
  onContactSelect: (contact: ContactJson) => void
}): ReactElement {
  const [searchParams] = useSearchParams()
  const [selectedContactId, setSelectedContactId] = useState<string | undefined>()
  const [query, setQuery] = useState<string>(``)
  const [loading, setLoading] = useState<boolean>(false)
  const [contacts, setContacts] = useState<ContactJson[]>([])

  const trimmedQuery = query.trim()
  const phoneCandidate = usPhoneNumber(trimmedQuery)

  useEffect(() => {
    if (!trimmedQuery) {
      setContacts([])
      setLoading(false)
      return
    }
    setLoading(true)
    const run = debounce(() => {
      bg(async () => {
        try {
          const phoneDigits = trimmedQuery.replace(/\D/g, ``)
          const phoneClause = phoneDigits ? `cellPhone:*${phoneDigits}*` : ``
          const nameClause = `name:(*${trimmedQuery}*)`
          const searchClause = phoneClause ? `(${nameClause} OR ${phoneClause})` : nameClause
          const locationClause = searchParams.has(`locationId`)
            ? ` AND locationId:${searchParams.get(`locationId`)!}`
            : ``
          const response = await gqlFetchList<ContactJson>(
            `contacts`,
            {
              search: `${searchClause} AND _exists_:cellPhone${locationClause}`,
              pagination: { limit: 50 },
              sort: { field: `name.keyword`, direction: `ASC` },
            },
            `id name cellPhone locationId lifecycleStage leadStatus`,
          )
          setContacts(response.hits)
        } catch (err) {
          logError(`Failed to fetch contacts for new conversation`, err)
          notifyError(`Failed to fetch contacts`)
          setContacts([])
        } finally {
          setLoading(false)
        }
      })
    }, 250)
    run()
    return () => run.cancel()
  }, [trimmedQuery, searchParams])

  const selected = useMemo(() => contacts.find((c) => c.id === selectedContactId), [contacts, selectedContactId])

  const canStart = !!selected || !!phoneCandidate

  const handleStart = () => {
    if (selected) {
      onContactSelect(selected)
      closeModal()
      return
    }
    if (phoneCandidate) {
      onContactSelect({
        id: undefined,
        cellPhone: phoneCandidate,
        name: phoneCandidate,
        locationId: searchParams.get(`locationId`) ?? undefined,
      })
      closeModal()
      return
    }
    notifyError(`Please select a contact or enter a phone number`)
  }

  return (
    <div className="flex w-full flex-col">
      <div className="px-6 pb-4 pt-2">
        <div className="flex h-10 items-center gap-x-2 rounded-md border border-default bg-surface px-3 focus-within:border-primary-500">
          <SearchIcon className="h-5 w-5 shrink-0 text-subtle" strokeWidth={2} />
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedContactId(undefined)
            }}
            placeholder="Search by name or phone number"
            className="min-w-0 flex-1 border-none bg-transparent p-0 text-body-sm text-strong outline-none placeholder:text-minimal focus:ring-0"
          />
        </div>
      </div>

      <div className="min-h-[360px] flex-1 border-y border-subtle bg-surface-subtle px-4 py-4">
        {!trimmedQuery ? (
          <EmptyHint />
        ) : loading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner size={10} />
          </div>
        ) : contacts.length > 0 ? (
          <div className="flex flex-col gap-y-2">
            <p className="px-1 text-label-xs font-semibold uppercase tracking-wide text-subtle">
              {contacts.length} {contacts.length === 1 ? `match` : `matches`}
            </p>
            {contacts.map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                selected={contact.id === selectedContactId}
                messageCount={conversations[contact.id!] ?? 0}
                onSelect={() => setSelectedContactId(contact.id)}
              />
            ))}
          </div>
        ) : (
          <NoMatches phoneCandidate={phoneCandidate} query={trimmedQuery} />
        )}
      </div>

      <div className="flex items-center justify-between bg-surface px-6 py-4">
        <div className="flex min-w-0 items-center gap-x-2 text-label-xs text-subtle">
          <WarningTriangle className="h-4 w-4 shrink-0 text-amber-500" strokeWidth={2} />
          <span>
            Multiple contacts can share a phone number — make sure the selected contact is the intended recipient.
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-x-2 pl-3">
          <Button variant="tertiary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canStart} onClick={handleStart}>
            Start conversation
          </Button>
        </div>
      </div>
    </div>
  )
}

function EmptyHint() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-y-2 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-500">
        <SearchIcon className="h-5 w-5" strokeWidth={2} />
      </span>
      <p className="text-body-sm font-semibold text-strong">Search for a contact</p>
      <p className="max-w-xs text-label-xs text-subtle">
        Look up an existing contact by name or phone number, or type a full 10-digit number to start a new conversation.
      </p>
    </div>
  )
}

function NoMatches({ phoneCandidate, query }: { phoneCandidate?: string; query: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-y-3 px-6 text-center">
      <p className="text-body-sm font-semibold text-strong">No matches for &ldquo;{query}&rdquo;</p>
      {phoneCandidate ? (
        <p className="max-w-sm text-label-xs text-subtle">
          You can start a new conversation with{` `}
          <span className="font-medium text-default">{formatPhoneNumber(phoneCandidate) ?? phoneCandidate}</span>
          {` `}using the button below.
        </p>
      ) : (
        <p className="max-w-sm text-label-xs text-subtle">
          Try a different name, or type a full 10-digit phone number to start a conversation with a new number.
        </p>
      )}
    </div>
  )
}

function ContactRow({
  contact,
  selected,
  messageCount,
  onSelect,
}: {
  contact: ContactJson
  selected: boolean
  messageCount: number
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={classNames(
        `group flex w-full items-center gap-3 rounded-lg border bg-surface p-3 text-left transition-colors`,
        selected
          ? `border-primary-500 ring-1 ring-primary-500`
          : `border-default hover:border-primary-300 hover:bg-primary-50/30`,
      )}
    >
      <ContactNameIcon contact={contact} widthClassName="w-9" heightClassName="h-9" sizeStyle={{ fontSize: 12 }} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-body-sm font-semibold text-strong">{contact.name}</div>
        <div className="mt-0.5 flex items-center gap-x-1.5 truncate text-label-xs text-subtle">
          {contact.cellPhone && (
            <span className="inline-flex items-center gap-x-1">
              <PhoneIcon className="h-3 w-3" strokeWidth={2} />
              {formatPhoneNumber(contact.cellPhone) ?? contact.cellPhone}
            </span>
          )}
          {contact.lifecycleStage && (
            <>
              <span className="text-minimal">·</span>
              <span className="font-medium text-green-600">{contact.lifecycleStage}</span>
            </>
          )}
          {messageCount > 0 && (
            <>
              <span className="text-minimal">·</span>
              <span>{messageCount} prior</span>
            </>
          )}
        </div>
      </div>
      <span
        aria-hidden
        className={classNames(
          `flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors`,
          selected ? `border-primary-500 bg-primary-500` : `border-strong bg-surface group-hover:border-primary-400`,
        )}
      >
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-surface" />}
      </span>
    </button>
  )
}
